import { Api, Fixtuur } from '@fixtuur/backpack';
import { useCachingAsyncCallback } from '../useCachingAsyncCallback';
import * as TE from 'fp-ts/TaskEither';
import * as Hooks from 'preact/hooks';
import { pipe } from 'fp-ts/lib/function';

/**
 * Given a proposed selection, attempt to apply it and return the resulting actual selections
 * Naturally if the new selection is incompatible with current selections, the API will
 * perform compatibility resolution
 */
export function useCachingSelectionResolver(
    serviceConfig: Api.ServiceConfig | undefined,
    spaceId: string | undefined,
    productSelections: Fixtuur.Configuration.Selections.Selections | null,
    compatiblePropertyOptions: Fixtuur.Configuration.Options.Options | null | undefined,
    fixedSelections: Fixtuur.Configuration.Selections.Selections,
    fallbacks: Fixtuur.Configuration.Selections.Selections[] | null,
    isFallbacksLoading: boolean,
):
    | ((
          property: string,
          value: Fixtuur.Configuration.Option.Value,
      ) => Promise<Fixtuur.Configuration.Selections.Selections>)
    | undefined {
    const resolveSelectionsCached = useCachingAsyncCallback(
        async (property, value, compatiblePropertyOptions, productSelections) =>
            // these can never be unset at this stage because we don't return the callback
            // unless they're defined (see return statement), but we need to make the
            // compiler happy.
            spaceId && serviceConfig && !isFallbacksLoading && fallbacks !== null
                ? await resolveChoice(
                      serviceConfig,
                      spaceId,
                      property,
                      value,
                      productSelections,
                      compatiblePropertyOptions,
                      fixedSelections,
                      fallbacks,
                  )
                : Fixtuur.Configuration.Selections.empty(), // Should never happen
        [serviceConfig, spaceId, fixedSelections, fallbacks, isFallbacksLoading], // if these change, we will lose our entire resolveChoice cache
    );

    /**
     * Why a second callback?
     *
     * If we didn't, every time compatiblePropertyOptions or productSelections changed,
     * we'd lose all of our historic cached resolveChoice calls.
     *
     * We only want to lose our cache if fundamental app-level dependencies change (serviceConfig etc),
     * dependencies which we don't expect to be modified during any running of the app.
     *
     * But we expect compatiblePropertyOptions and productSelections to change often, so
     * we want them to remain part of our cached resolveChoice calls.
     */
    const resolveSelectionsFinal = Hooks.useCallback(
        async (property: string, value: Fixtuur.Configuration.Option.Value) =>
            compatiblePropertyOptions && productSelections
                ? await resolveSelectionsCached(
                      property,
                      value,
                      compatiblePropertyOptions,
                      productSelections,
                  )
                : Fixtuur.Configuration.Selections.empty(),
        [resolveSelectionsCached, compatiblePropertyOptions, productSelections], // if these change, we will not lose our resolveChoice cache
    );

    const dependenciesLoaded =
        spaceId &&
        serviceConfig &&
        compatiblePropertyOptions &&
        productSelections &&
        !isFallbacksLoading;

    return dependenciesLoaded ? resolveSelectionsFinal : undefined;
}

async function resolveChoice(
    serviceConfig: Api.ServiceConfig,
    spaceId: string,
    property: string,
    value: Fixtuur.Configuration.Option.Value,
    productSelections: Fixtuur.Configuration.Selections.Selections,
    compatiblePropertyOptions: Fixtuur.Configuration.Options.Options,
    fixedSelections: Fixtuur.Configuration.Selections.Selections,
    fallbacks: Fixtuur.Configuration.Selections.Selections[],
): Promise<Fixtuur.Configuration.Selections.Selections> {
    if (
        Fixtuur.Configuration.Options.isOptionIncluded(compatiblePropertyOptions, property, value)
    ) {
        /** If the proposal is compatible anyway, no need to make the call just select and go */
        return { ...productSelections, [property]: value };
    } else {
        /**
         * Proposals are sent to the API - they represent our priority list for when the
         * incompatibility is being resolved.
         */
        const proposal = { [property]: value };
        const proposals = [fixedSelections, proposal, productSelections, ...(fallbacks ?? [])]
            .flatMap(Object.entries)
            .map(([key, val]) => ({
                [key]: val,
            }));

        return pipe(
            Api.Configuration.Selections.fromProposal(serviceConfig, spaceId, proposals),
            TE.match(
                error => {
                    throw error;
                },
                selections => Fixtuur.Configuration.Selections.create(selections.selections),
            ),
        )();
    }
}
